import { useTranslation, UseTranslationOptions } from "react-i18next";
import { useCallback } from "react";
import locale from "@/utils/locale";
import type { SupportedLanguage } from "./i18n.types";

export const useI18n = (
  ns?: string | string[],
  options?: UseTranslationOptions<string>
) => {
  const { t, i18n, ready } = useTranslation(ns, options);

  /**
   * 优先使用 i18next 的翻译，找不到时 fallback 到 locale.ts
   */
  const translate = useCallback(
    (key: string, params?: any): string => {
      // 检查 i18next 中是否存在该 key
      if (i18n.exists(key)) {
        return String(t(key, params));
      }

      // i18next 中不存在，尝试使用 locale.ts
      const localeValue = locale.get(key, params);
      if (localeValue && localeValue !== key) {
        return localeValue;
      }

      // 都没找到，返回原始 key
      return key;
    },
    [t, i18n]
  );

  /**
   * 切换语言
   */
  const changeLanguage = useCallback(
    async (lang: SupportedLanguage) => {
      try {
        await i18n.changeLanguage(lang);
        return true;
      } catch (error) {
        console.error("Failed to change language:", error);
        return false;
      }
    },
    [i18n]
  );

  /**
   * 获取当前语言
   */
  const currentLanguage = i18n.language as SupportedLanguage;

  return {
    t: translate,
    i18n,
    ready,
    currentLanguage,
    changeLanguage,
  };
};

/**
 * 简化版的翻译 hook，只返回翻译函数
 */
export const useT = (ns?: string | string[]) => {
  const { t } = useI18n(ns);
  return t;
};

/**
 * 语言切换 hook
 */
export const useLanguage = () => {
  const { currentLanguage, changeLanguage } = useI18n();

  return {
    language: currentLanguage || "en",
    changeLanguage,
  };
};
